import React from "react";
import { Users, ShieldCheck, Leaf, Check } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { SectionHead } from "@/components/SectionHead";
import { TrustBar } from "@/components/TrustBar";
import { Reveal, Stagger, StaggerItem, ParallaxImage, Marquee } from "@/lib/motion";
import { VALUES, PROMISES } from "@/data/content";
import { IMG, SEAL_GOLD } from "@/data/images";
import { useImg } from "@/lib/useImg";

const CIRCLE_ICONS = [Users, ShieldCheck, Leaf];

const COMMITMENTS = ["We source responsibly.", "We test rigorously.", "We craft carefully.", "We package mindfully.", "We deliver honestly."];

export default function QualityStandard() {
  const img = useImg();
  return (
    <div data-testid="quality-standard-page">
      <PageHero
        tall
        eyebrow="Our Quality Standard"
        lines={["Trust isn't claimed.", "It's earned."]}
        sub="At Nutribest, we don't just make superfoods. We make a promise — to you, to your family, and to the future we all share."
        image={img("heroQuality")}
      />

      <section className="py-20 sm:py-28">
        <div className="container-nb">
          <SectionHead title="Our promise is simple." />
          <Stagger className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {PROMISES.map((text, i) => {
              const Icon = CIRCLE_ICONS[i];
              return (
                <StaggerItem key={text}>
                  <div data-testid={`promise-statement-${i + 1}`} className="text-center px-6 py-10 group">
                    <span className="mx-auto w-20 h-20 rounded-full border border-gold/40 bg-white flex items-center justify-center text-forest group-hover:bg-forest group-hover:text-cream group-hover:scale-105 transition-all duration-500">
                      <Icon size={28} strokeWidth={1.4} />
                    </span>
                    <p className="mt-6 font-serif text-lg sm:text-xl text-forest leading-snug">{text}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      <section className="py-20 sm:py-24 bg-creamdark/40 border-y border-forest/10">
        <div className="container-nb">
          <SectionHead eyebrow="What We Promise" title="Six uncompromising standards." />
          <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-forest/10 rounded-2xl overflow-hidden border border-forest/10">
            {VALUES.map(({ icon: Icon, title, text }) => (
              <StaggerItem key={title} className="bg-cream">
                <div className="h-full p-6 text-center hover:bg-white transition-colors duration-500">
                  <span className="mx-auto w-12 h-12 rounded-full border border-forest/15 flex items-center justify-center text-forest"><Icon size={20} strokeWidth={1.5} /></span>
                  <h3 className="mt-4 text-[11px] font-bold uppercase tracking-[0.14em] text-forest">{title}</h3>
                  <p className="mt-2 text-[11px] text-mutedgreen leading-relaxed">{text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="relative py-20 sm:py-28 bg-forestdeep grain overflow-hidden">
        <div className="container-nb grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl text-cream">Our Commitment. Every Day.</h2>
            <ul className="mt-8 space-y-4">
              {COMMITMENTS.map((c) => (
                <li key={c} className="flex items-center gap-3.5 text-cream/90">
                  <span className="w-6 h-6 rounded-full bg-gold flex items-center justify-center shrink-0"><Check size={13} strokeWidth={3} className="text-white" /></span>
                  <span className="font-serif text-lg">{c}</span>
                </li>
              ))}
            </ul>
            <div className="w-10 h-px bg-goldlight/50 my-8" />
            <p className="text-cream/70 text-sm leading-relaxed max-w-sm">Because our promise is not just a statement — it's a standard we live by, every single day.</p>
          </Reveal>
          <Reveal delay={0.15}>
            <ParallaxImage src={img("moringaBowl")} alt="Fresh moringa powder in a wooden bowl" className="rounded-2xl h-72 lg:h-[420px] shadow-2xl" speed={10} />
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="container-nb">
          <Reveal>
            <div data-testid="promise-seal-card" className="bg-white border border-forest/10 rounded-3xl p-8 sm:p-12 grid lg:grid-cols-[auto_1fr] gap-10 items-center shadow-xl shadow-forest/5">
              <img src={SEAL_GOLD} alt="The Nutribest Promise Seal" className="w-44 h-44 sm:w-56 sm:h-56 object-cover rounded-full mix-blend-multiply mx-auto animate-float-soft" />
              <div>
                <p className="eyebrow mb-3">The Nutribest Promise Seal</p>
                <h2 className="font-serif text-2xl sm:text-3xl text-forest leading-snug">Every product that carries this seal has met our six uncompromising standards.</h2>
                <p className="mt-4 text-sm text-mutedgreen leading-relaxed max-w-xl">It's our way of saying — this is our promise to you: trust, quality, and purity in every pack.</p>
                <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                  {["Pure Ingredients", "Uncompromising Quality", "Complete Transparency", "Responsible Sourcing", "Rigorous Testing", "Safe & Sustainable Packaging"].map((s) => (
                    <span key={s} className="flex items-center gap-2 text-xs text-forest/80 font-medium"><span className="w-1.5 h-1.5 rounded-full bg-gold" /> {s}</span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative py-24 sm:py-32 overflow-hidden">
        <ParallaxImage src={img("heroFarm")} alt="" className="absolute inset-0" speed={12} />
        <div className="absolute inset-0 bg-forestdeep/75" />
        <div className="relative container-nb max-w-3xl">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-5xl text-cream leading-tight">We hope to<br />earn your trust.</h2>
            <p className="mt-6 text-cream/80 text-sm sm:text-base leading-relaxed max-w-md">Your trust is the reason we exist, and the responsibility we carry in everything we do.</p>
            <p className="mt-6 font-serif italic text-goldlight text-lg">— The Nutribest Family</p>
          </Reveal>
        </div>
      </section>

      <Marquee items={["Lab tested for safety.", "100% natural ingredients.", "No artificial additives.", "Made with care in India.", "Loved by families worldwide."]} />
      <TrustBar />
    </div>
  );
}
